import React, { useState } from 'react';
import { MessageSquare, Copy, Check, RefreshCw, Send } from 'lucide-react';
import { Contact, ResponseSuggestion } from '../types';
import { aiService } from '../services/aiService';

interface ResponseSuggestionAIProps {
  contact: Contact;
  context?: string;
  onUseResponse?: (response: string) => void;
}

const ResponseSuggestionAI: React.FC<ResponseSuggestionAIProps> = ({
  contact,
  context = 'initial_inquiry',
  onUseResponse
}) => {
  const [suggestion, setSuggestion] = useState<ResponseSuggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editedResponse, setEditedResponse] = useState('');

  // Rate limiting is enforced server-side in openai-proxy.

  const generateSuggestion = async () => {
    setLoading(true);
    try {
      const aiSuggestion = await aiService.generateResponseSuggestion(context, contact);
      setSuggestion(aiSuggestion);
      setEditedResponse(aiSuggestion.suggestedResponse);
    } catch (error) {
      console.error('Error generating suggestion:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(editedResponse).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getToneColor = (tone: ResponseSuggestion['tone']) => {
    switch (tone) {
      case 'professional': return 'bg-blue-100 text-blue-800';
      case 'friendly': return 'bg-green-100 text-green-800';
      case 'urgent': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getToneLabel = (tone: ResponseSuggestion['tone']) => {
    switch (tone) {
      case 'professional': return 'Profesional';
      case 'friendly': return 'Amigable';
      case 'urgent': return 'Urgente';
      default: return 'Neutral';
    }
  };

  return (
    <>
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Sugerencia de Respuesta IA</h3>
              <p className="text-sm text-gray-500">Respuesta personalizada para {contact.fullName}</p>
            </div>
          </div>
          <button
            onClick={generateSuggestion}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span>{loading ? 'Generando...' : 'Generar'}</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Generando respuesta personalizada...</p>
          </div>
        ) : suggestion ? (
          <div className="space-y-4">
            {/* Suggestion Metadata */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getToneColor(suggestion.tone)}`}>
                  {getToneLabel(suggestion.tone)}
                </span>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {suggestion.confidence}% confianza
                </span>
              </div>
            </div>

            {/* Editable Response */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Respuesta Sugerida (Editable)
              </label>
              <textarea
                value={editedResponse}
                onChange={(e) => setEditedResponse(e.target.value)}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="La respuesta aparecerá aquí..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copied ? 'Copiado!' : 'Copiar'}</span>
                </button>
                
                <button
                  onClick={generateSuggestion}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Nueva Sugerencia</span>
                </button>
              </div>

              {onUseResponse && (
                <button
                  onClick={() => onUseResponse(editedResponse)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Send className="w-4 h-4" />
                  <span>Usar Respuesta</span>
                </button>
              )}
            </div>

            {/* Context Selector */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Tipo de Mensaje</h4>
              <div className="grid grid-cols-2 gap-2">
                {contact.leadStatus === 'ya_es_cliente' ? [
                  { value: 'client_checkin', label: 'Check-in Cliente' },
                  { value: 'client_feedback', label: 'Solicitar Feedback' },
                  { value: 'upsell', label: 'Oportunidad Upsell' },
                  { value: 'renewal', label: 'Renovación Contrato' }
                ] : contact.leadStatus === 'nuevo' ? [
                  { value: 'initial_inquiry', label: 'Consulta Inicial' },
                  { value: 'welcome', label: 'Bienvenida' },
                  { value: 'qualification', label: 'Calificación' },
                  { value: 'info_request', label: 'Envío Info' }
                ] : contact.leadStatus === 'en_seguimiento' ? [
                  { value: 'follow_up', label: 'Seguimiento' },
                  { value: 'proposal', label: 'Envío Propuesta' },
                  { value: 'meeting_request', label: 'Solicitar Reunión' },
                  { value: 'urgent_follow_up', label: 'Urgente' }
                ] : [
                  { value: 'initial_inquiry', label: 'Consulta Inicial' },
                  { value: 'follow_up', label: 'Seguimiento' },
                  { value: 'urgent_follow_up', label: 'Urgente' },
                  { value: 'reactivation', label: 'Reactivación' }
                ].map(ctx => (
                  <button
                    key={ctx.value}
                    onClick={() => {
                      // Track context change as AI usage
                      trackAIUsage('ResponseSuggestionAI', 'change_context');
                      
                      // Check if blocked before generating
                      if (shouldBlockAI()) {
                        console.log('🚫 AI Context change blocked due to abuse');
                        return;
                      }
                      
                      // Update context and regenerate
                      generateSuggestion();
                    }}
                    className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                      context === ctx.value
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {ctx.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Genera una Respuesta IA</h4>
            <p className="text-gray-500 mb-4">
              La IA creará una respuesta personalizada basada en la información del contacto
            </p>
            <button
              onClick={generateSuggestion}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Generar Respuesta
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ResponseSuggestionAI;